import {
  SECURE_NOTE_DISCLAIMER_TEXT,
  canBuyCredits,
  formatAddress,
  getClientDisplayFullName,
  hasCredits,
} from '@frontend/api-client';
import type { SecureNote } from '@frontend/api-types';
import { dateFormatters, e164ToInternational, formatCurrencyNatural, pluralize } from '@frontend/duck-tape';
import { useEffect, useFeatureIsOn, useState, useVisibilityController } from '@frontend/react';
import {
  Button,
  Divider,
  EmptyList,
  FlatList,
  Icon,
  LinkButton,
  PageContainer,
  SkeletonList,
  TabPillList,
  Text,
  XStack,
  YStack,
} from '@frontend/web-react';
import { CreateSecureNoteModal, CurrentMembershipCard, UpdateSecureNoteModal } from '@frontend/web/components';
import { BuyCreditsModal } from '@frontend/web/components/DomainSpecific/Payment/BuyCreditsModal';
import {
  MembershipCredits,
  MonthlyBandwidth,
  UpdatePrimaryAddressModal,
  UpdateProfileModal,
} from '@frontend/web/components/DomainSpecific/Settings';
import { AccountDetailsModal } from '@frontend/web/components/DomainSpecific/Settings/AccountDetailsModal';
import { usePricingTerminology } from '@frontend/web/context';
import {
  useAccessControl,
  useLazyListPurchasesQuery,
  useLazyListSecureNotesQuery,
  useListAddressesQuery,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
  useRetrieveCreditsQuery,
} from '@frontend/web/hooks';
import { router } from '@frontend/web/router';
import { createFileRoute } from '@tanstack/react-router';

const tabItems = [
  { key: 'secureInfo', label: 'Secure Info' },
  { key: 'payments', label: 'Payments' },
] as const;

type TabKey = (typeof tabItems)[number]['key'];

const Profile = () => {
  const { data: client, isLoading: isClientLoading } = useRetrieveClientQuery();
  const { data: addresses, isLoading: isAddressesLoading } = useListAddressesQuery();
  const { data: clientStatus, isLoading: isClientStatusLoading } = useRetrieveClientStatusQuery();
  const { isClientSubscribed } = useAccessControl();
  const [listSecureNotes, { data: secureNotes, isLoading: isListSecureNotesLoading }] = useLazyListSecureNotesQuery();
  const [listPurchases, { data: purchases, isLoading: isListPurchasesLoading }] = useLazyListPurchasesQuery();
  const { enabled: isSelfServicePlanMgmtEnabled } = useFeatureIsOn('is_self_service_plan_mgmt_enabled');
  const [activeTab, setActiveTab] = useState<TabKey>('secureInfo');
  const [selectedSecureNote, setSelectedSecureNote] = useState<SecureNote>();

  const [isEditSecureNoteModalOpen, { close: closeEditSecureNoteModal, open: openEditSecureNoteModal }] =
    useVisibilityController();
  const [isCreateSecureNoteModalOpen, { close: closeCreateSecureNoteModal, open: openCreateSecureNoteModal }] =
    useVisibilityController();
  const [isEditProfileModalOpen, { close: closeEditProfileModal, open: openEditProfileModal }] =
    useVisibilityController();
  const [isEditAddressModalOpen, { close: closeEditAddressModal, open: openEditAddressModal }] =
    useVisibilityController();
  const [isAccountDetailsModalOpen, { close: closeAccountDetailsModal, open: openAccountDetailsModal }] =
    useVisibilityController();
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);

  const { data: credits, refetch: refetchCredits } = useRetrieveCreditsQuery();
  const { pricingTerminology, showCredits } = usePricingTerminology();

  useEffect(() => {
    if (client?.id) {
      listSecureNotes({ clientId: client?.id });
      listPurchases({ clientId: client?.id });
    }
  }, [client?.id]);

  const address = addresses?.[0];

  return (
    <>
      <PageContainer
        HeaderComponent={
          <YStack bg="neutral50" className="py-md px-md">
            <XStack className="justify-end">
              {canBuyCredits({ client, clientStatus, credits }) ? (
                <Button
                  label={
                    showCredits && hasCredits(credits)
                      ? `${pluralize(pricingTerminology.unit_term, credits.availableCredits, true)}`
                      : pricingTerminology.add_button
                  }
                  onClick={() => setIsBuyCreditsModalOpen(true)}
                  size="sm"
                  variant="secondary"
                />
              ) : null}
            </XStack>
          </YStack>
        }
        LoadingComponent={<SkeletonList className="pt-lg px-lg" countSkeletons={6} />}
        contentContainerClassName="flex-1 overflow-y-scroll"
        isFlexibleHeight
        isLoading={
          !client ||
          isClientLoading ||
          isAddressesLoading ||
          isListPurchasesLoading ||
          isListSecureNotesLoading ||
          isClientStatusLoading
        }
        isScrollable
        padding="none"
      >
        <YStack className="w-full items-center">
          <YStack className="max-w-screen-md w-full">
            <XStack className="pt-lg pb-sm px-md justify-between items-center">
              <Text type="h2">{client ? getClientDisplayFullName(client) : ''}</Text>
            </XStack>
            <YStack className="px-md py-sm gap-sm">
              <XStack className="gap-xs items-center">
                <Icon color="secondary" name="IconMail" size="sm" />
                <Text color="secondary" type="caption">
                  {client?.email}
                </Text>
              </XStack>
              <XStack alignItems="center" gapX="xs">
                <Icon color="secondary" name="IconPhone" size="sm" />
                {client?.phoneNumber ? (
                  <Text color="secondary" type="caption">
                    {e164ToInternational(client.phoneNumber) ?? 'Invalid phone number'}
                  </Text>
                ) : null}
              </XStack>
              {address ? (
                <XStack alignItems="center" gapX="xs">
                  <Icon color="secondary" name="IconHome" size="sm" />
                  <Text color="secondary" type="caption">
                    {address ? formatAddress(address) : null}
                  </Text>
                </XStack>
              ) : null}
              {client?.birthday ? (
                <XStack alignItems="center" gapX="xs">
                  <Icon color="secondary" name="IconCake" size="sm" />
                  <Text color="secondary" type="caption">
                    {dateFormatters['MM/DD/YYYY'](client.birthday)}
                  </Text>
                </XStack>
              ) : null}
            </YStack>
            <XStack className="px-md justify-start py-sm gap-sm">
              <Button label="Edit profile" onClick={openEditProfileModal} variant="secondary" />
              {address ? <Button label="Edit address" onClick={openEditAddressModal} variant="secondary" /> : null}
            </XStack>
            <YStack className="px-md py-xl">
              {credits && isClientSubscribed ? (
                !showCredits ? (
                  <MonthlyBandwidth credits={credits} onCreditsRefetch={refetchCredits} />
                ) : (
                  <MembershipCredits
                    credits={credits}
                    isNewAccountDetailsPageEnabled={false}
                    onAccountPress={openAccountDetailsModal}
                    onCreditsRefetch={refetchCredits}
                  />
                )
              ) : null}
              {isSelfServicePlanMgmtEnabled ? (
                <YStack gap="sm" py="lg">
                  <Text>Current Membership</Text>
                  <CurrentMembershipCard />
                </YStack>
              ) : null}
            </YStack>
            <Divider className="border-t-neutral-100" size={4} />
            <YStack className="px-md gap-md overflow-auto pb-lg">
              <TabPillList activeKey={activeTab} className="py-md" onTabChange={setActiveTab} tabs={tabItems} />
              {activeTab === 'secureInfo' ? (
                <YStack>
                  <FlatList
                    ListEmptyComponent={
                      <EmptyList
                        // eslint-disable-next-line tailwindcss/no-arbitrary-value
                        className="h-auto gap-y-sm min-h-[200px]"
                        iconName="IconLock"
                        subLabel={SECURE_NOTE_DISCLAIMER_TEXT}
                      />
                    }
                    ListFooterComponent={
                      isClientSubscribed ? (
                        <YStack className="items-start mt-md">
                          <LinkButton
                            label="Add secure info"
                            leftIconName="IconPlus"
                            leftIconProps={{
                              className: 'bg-neutral-100 items-center justify-center rounded-full size-[36px]',
                              size: 24,
                            }}
                            onClick={openCreateSecureNoteModal}
                            textClassName="text-p-sm"
                            withUnderline
                          />
                        </YStack>
                      ) : null
                    }
                    accessibilityLabel="Secure notes list"
                    data={secureNotes}
                    keyExtractor={(item) => item.id}
                    renderItem={({ item }) => (
                      <XStack
                        alignItems="center"
                        enableHoverEffect
                        gapX="sm"
                        onClick={() => {
                          setSelectedSecureNote(item);
                          openEditSecureNoteModal();
                        }}
                        py="md"
                      >
                        <XStack alignItems="center" className="flex-1" gapX="sm">
                          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                          <Icon
                            // eslint-disable-next-line tailwindcss/no-arbitrary-value
                            className="size-[40px] rounded-full bg-neutral-100 items-center justify-center"
                            color="tertiary"
                            name="IconLock"
                            size={24}
                          />
                          <Text type="h6">{item.title}</Text>
                        </XStack>
                        <Icon color="slate300" name="IconChevronRight" />
                      </XStack>
                    )}
                  />
                </YStack>
              ) : null}
              {activeTab === 'payments' ? (
                <FlatList
                  ListEmptyComponent={
                    <EmptyList
                      // eslint-disable-next-line tailwindcss/no-arbitrary-value
                      className="h-auto gap-y-sm min-h-[200px]"
                      iconName="IconShoppingBag"
                      subLabel="When Duckbill makes any purchases for you, it'll show up here."
                    />
                  }
                  accessibilityLabel="Purchases list"
                  contentContainerClassName="gap-sm"
                  data={purchases}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => (
                    <XStack
                      alignItems="center"
                      enableHoverEffect
                      gapX="sm"
                      onClick={() => router.navigate({ params: { taskId: item.task }, to: '/app/task/$taskId' })}
                      py="md"
                    >
                      <XStack alignItems="center" className="flex-1" gapX="sm">
                        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                        <Icon
                          // eslint-disable-next-line tailwindcss/no-arbitrary-value
                          className="size-[40px] rounded-full bg-neutral-100 items-center justify-center"
                          color="tertiary"
                          name="IconReceipt2"
                          size={24}
                        />
                        <YStack>
                          <Text type="h6">{item.item}</Text>
                          <Text color="secondary" type="p-sm">
                            {item.type}
                          </Text>
                        </YStack>
                      </XStack>
                      <XStack>
                        <Text type="h6">{formatCurrencyNatural(item.amount)}</Text>
                        <Icon color="slate300" name="IconChevronRight" />
                      </XStack>
                    </XStack>
                  )}
                />
              ) : null}
            </YStack>
            {selectedSecureNote ? (
              <UpdateSecureNoteModal
                isOpen={isEditSecureNoteModalOpen}
                onClose={closeEditSecureNoteModal}
                secureNote={selectedSecureNote}
                secureNoteId={selectedSecureNote.id}
                title={selectedSecureNote.title}
              />
            ) : null}
            {client ? (
              <CreateSecureNoteModal
                clientId={client.id}
                isOpen={isCreateSecureNoteModalOpen}
                onClose={closeCreateSecureNoteModal}
              />
            ) : null}
            {address ? (
              <UpdatePrimaryAddressModal
                isOpen={isEditAddressModalOpen}
                onClose={closeEditAddressModal}
                primaryAddress={address}
              />
            ) : null}
            <UpdateProfileModal isOpen={isEditProfileModalOpen} onClose={closeEditProfileModal} />
            <AccountDetailsModal isOpen={isAccountDetailsModalOpen} onClose={closeAccountDetailsModal} />
          </YStack>
        </YStack>
      </PageContainer>
      <BuyCreditsModal
        closeModal={() => setIsBuyCreditsModalOpen(false)}
        isOpen={isBuyCreditsModalOpen}
        onCreditsPurchased={() => {
          refetchCredits();
        }}
      />
    </>
  );
};

export const Route = createFileRoute('/app/profile')({
  component: Profile,
});
